import React from 'react';
import { Sparkles, Check, ChevronRight } from 'lucide-react';

interface UsageUpgradeCardProps {
  videosUsed: number;
  monthlyLimit: number;
  onUpgrade: () => void;
  className?: string;
  ctaLabel?: string;
}

const UsageUpgradeCard: React.FC<UsageUpgradeCardProps> = ({
  videosUsed,
  monthlyLimit,
  onUpgrade,
  className = '',
  ctaLabel = 'Upgrade to Pro'
}) => {
  const videosRemaining = Math.max(monthlyLimit - videosUsed, 0);
  const usagePercent = monthlyLimit > 0 ? Math.min((videosUsed / monthlyLimit) * 100, 100) : 0;

  return (
    <div className={`relative z-10 bg-white border border-red-100 rounded-3xl shadow-[0_20px_50px_rgba(15,23,42,0.08)] p-6 sm:p-8 flex flex-col md:flex-row gap-6 ${className}`}>
      <div className="flex-1">
        <div className="flex items-center space-x-3 text-xs font-semibold text-red-600 uppercase tracking-[0.3em]">
          <Sparkles size={14} />
          <span>Free Plan Usage</span>
        </div>
        <div className="flex flex-wrap items-baseline gap-2 mt-3">
          <h3 className="text-4xl font-display font-bold text-gray-900">
            {videosRemaining}
          </h3>
          <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            uploads left this month
          </span>
        </div>
        <p className="text-sm text-primary-600 mt-3">
          You’ve used <span className="font-semibold text-gray-900">{videosUsed}</span> of {monthlyLimit} uploads.
          Upgrade to unlock 50 uploads/month, remove watermarks, and jump the processing queue.
        </p>
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>{videosUsed} used</span>
            <span>{monthlyLimit} total</span>
          </div>
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-red-500 via-red-500 to-red-600 transition-all"
              style={{ width: `${usagePercent}%` }}
            />
          </div>
        </div>
      </div>
      <div className="md:w-56 w-full space-y-3">
        <div className="bg-red-50 border border-red-100 rounded-2xl p-4">
          <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">Pro unlocks</p>
          <ul className="space-y-1.5 text-sm text-gray-700">
            <li className="flex items-center">
              <Check size={14} className="text-red-500 mr-2" />50 uploads/month
            </li>
            <li className="flex items-center">
              <Check size={14} className="text-red-500 mr-2" />No watermarks
            </li>
            <li className="flex items-center">
              <Check size={14} className="text-red-500 mr-2" />Priority processing
            </li>
          </ul>
        </div>
        <button
          onClick={onUpgrade}
          className="w-full inline-flex items-center justify-center px-5 py-3 rounded-2xl bg-red-600 text-white font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
        >
          {ctaLabel}
          <ChevronRight size={16} className="ml-2" />
        </button>
      </div>
    </div>
  );
};

export default UsageUpgradeCard;

